import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select/dist/js/tom-select.complete.js"

export default class extends Controller {
  static values = {
    placeholder: String,
    noResultsText: String,
    invalid: Boolean,
    maxItems: Number,
    create: Boolean,
    createUrl: String,
    createText: String,
    createdValueField: String,
    productSearchUrl: String,
    productSearchWarehouseId: Number,
    productValuePrefix: String,
    options: Array
  }

  connect() {
    this.element.setAttribute("autocomplete", "off")
    this.productLoadRequestId = 0
    this.productEnterRequestId = 0

    this.select = new TomSelect(this.element, this.config)
    this.select.wrapper.classList.add("tom-select-input")
    if (this.invalidValue) this.select.wrapper.classList.add("is-invalid")
    if (this.element.autofocus) requestAnimationFrame(() => this.select.focus())
  }

  get config() {
    const config = {
      maxItems: this.maxItems,
      valueField: "value",
      labelField: "text",
      searchField: ["text"],
      openOnFocus: true,
      preload: true,
      placeholder: this.placeholderValue,
      render: this.renderers,
      onFocus() {
        this.refreshOptions(false)
        this.open()
      }
    }

    if (this.hasOptionsValue) config.options = this.optionsValue
    if (this.hasProductSearchUrlValue) this.configureProductSearch(config)

    if (this.element.multiple) {
      config.plugins = ["remove_button", "input_autogrow"]
      config.closeAfterSelect = true
      config.createOnBlur = true
      config.delimiter = ", "
    } else {
      config.plugins = {
        dropdown_input: {}
      }
      config.onItemAdd = function () {
        this.blur()
      }
    }
    if (this.createValue) {
      config.create = (input, callback) => this.createOption(input, callback)
      config.render.option_create = () => (
        `<div class="create text-primary-700 underline-offset-2 hover:underline">${this.escapeHtml(this.createText)}</div>`
      )
    }

    return config
  }

  disconnect() {
    this.productLoadRequestId += 1
    this.productEnterRequestId += 1
    this.select?.destroy()
  }

  renderOption(data, escape) {
    return `<div class="truncate font-medium">${escape(data.text || "")}</div>`
  }

  createOption(input, callback) {
    const title = input.trim()
    if (!title) {
      callback()
      return
    }

    if (!this.hasCreateUrlValue) {
      callback({ value: title, text: title })
      return
    }

    fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfToken,
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({ dictionary_value: { title } })
    })
      .then((response) => {
        if (!response.ok) throw new Error("TomSelect create failed")

        return response.json()
      })
      .then((data) => {
        callback({
          value: String(this.createdOptionValue(data, title)),
          text: String(data.text || data.title || title)
        })
      })
      .catch(() => callback())
  }

  configureProductSearch(config) {
    const controller = this

    config.preload = false
    config.loadThrottle = 200
    config.shouldLoad = (query) => query.trim().length >= 2
    config.load = (query, callback) => this.loadProductOptions(query, callback)
    config.onInitialize = function () {
      this.control_input.addEventListener("keydown", (event) => {
        controller.selectExactProductOnEnter(event, this)
      }, { capture: true })
    }
  }

  loadProductOptions(query, callback) {
    const requestId = ++this.productLoadRequestId
    this.fetchProductOptions(query)
      .then((options) => {
        if (requestId === this.productLoadRequestId) callback(options)
        else callback()
      })
      .catch(() => callback())
  }

  async fetchProductOptions(query) {
    const url = new URL(this.productSearchUrlValue, window.location.origin)
    url.searchParams.set("query", query.trim())
    url.searchParams.set("warehouse_id", this.productSearchWarehouseIdValue)

    const response = await fetch(url, { headers: { Accept: "application/json" } })
    if (!response.ok) throw new Error("Product search failed")

    const data = await response.json()
    const exactId = data.exact_product ? String(data.exact_product.id) : null
    const products = [data.exact_product, ...(data.products || [])].filter(Boolean)
    const uniqueProducts = [...new Map(products.map((product) => [String(product.id), product])).values()]

    return uniqueProducts.map((product) => ({
      value: `${this.productValuePrefixValue}${product.id}`,
      text: [product.title, product.sku, product.barcode].filter(Boolean).join(" · "),
      exact: String(product.id) === exactId
    }))
  }

  selectExactProductOnEnter(event, select) {
    const query = select.control_input.value.trim()
    if (event.key !== "Enter" || query.length < 2) return

    event.preventDefault()
    event.stopImmediatePropagation()
    const requestId = ++this.productEnterRequestId
    this.fetchProductOptions(query)
      .then((options) => {
        if (requestId !== this.productEnterRequestId || select.control_input.value.trim() !== query) return

        options.forEach((option) => select.addOption(option))
        const exactOption = options.find((option) => option.exact)
        if (exactOption) {
          select.setValue(exactOption.value)
          select.close()
          return
        }

        select.refreshOptions(false)
        const activeValue = select.activeOption?.dataset.value
        if (activeValue) {
          select.setValue(activeValue)
          select.close()
        } else if (select.canCreate(query)) {
          select.createItem(query)
        }
      })
      .catch(() => {
        if (requestId === this.productEnterRequestId && select.control_input.value.trim() === query) select.open()
      })
  }

  createdOptionValue(data, fallback) {
    if (this.hasCreatedValueFieldValue) return data[this.createdValueFieldValue] || fallback

    return data.value || data.id || fallback
  }

  get createText() {
    if (this.hasCreateTextValue) return this.createTextValue

    return ""
  }

  get renderers() {
    const renderers = {
      option: (data, escape) => this.renderOption(data, escape),
      item: (data, escape) => this.renderOption(data, escape)
    }

    if (this.hasNoResultsTextValue) {
      renderers.no_results = () => `<div class="no-results">${this.escapeHtml(this.noResultsTextValue)}</div>`
    }

    return renderers
  }

  get maxItems() {
    if (this.hasMaxItemsValue) return this.maxItemsValue
    if (this.element.multiple) return null

    return 1
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }
}
